import { DevToolsMenu } from 'dev-tools';
import { useConfig } from 'providers/config/ConfigProvider';
import { PropsWithChildren } from 'react';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import ReactGA from 'react-ga4';
import { Outlet } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';
import { ToastHolder } from '@sanlam-glacier/app-common/modules/toasts';
import style from './holders.module.scss';
import { RouteBoundary } from '../molecules/route-boundary/RouteBoundary';
import { QueryProvider } from 'providers/query-provider/QueryProvider';
import { IdleLogoutGuard } from '../molecules/idle-logout-guard/IdleLogoutGuard';
import { useAuth } from 'auth';

const GoogleAnalyticsProvider = ({ children }: PropsWithChildren) => {
  const trackingId = useConfig()?.googleAnalytics?.trackingId ?? '';
  const auth = useAuth();

  /**
   * Defensive import for react-ga4 to handle CommonJS/ESM interop differences.
   * Vite 8 uses Rolldown for module resolution, which can expose CommonJS modules
   * with their exports under a `default` property. This guard safely resolves
   * the initialize function from either shape to prevent runtime errors.
   */
  const ga = ReactGA as unknown as {
    initialize?: (id: string, opts?: unknown) => void;
    default?: { initialize?: (id: string, opts?: unknown) => void };
  };

  const initialize = ga?.initialize ?? ga?.default?.initialize;

  if (typeof initialize === 'function' && trackingId) {
    initialize(trackingId, {
      gaOptions: {
        userId: auth.getSignedInUser()?.username
      }
    });
  }

  return children;
};

const ConfiguredQueryProvider = ({ children }: PropsWithChildren) => {
  const config = useConfig();

  return (
    <QueryProvider
      bffApiUrl={replaceWithDynamicHost(config.apiUrl, config.apiHostMappings)}
      localJwtProvider={config.auth.afSso.jwtProvider}
      configureAccessToken={true}
    >
      {children}
    </QueryProvider>
  );
};

export const AuthenticatedLayout = () => {
  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <IdleLogoutGuard>
        <GoogleAnalyticsProvider>
          <ConfiguredQueryProvider>
            <RouteBoundary>
              <DevToolsMenu />
              <Outlet />
            </RouteBoundary>
            <ToastHolder className={style.customToastHolder} />
          </ConfiguredQueryProvider>
        </GoogleAnalyticsProvider>
      </IdleLogoutGuard>
    </QueryParamProvider>
  );
};

export default AuthenticatedLayout;
